import {useSharedFilter} from '../../shared/navigation/useSharedReport';
// src/app/layout/MainLayout.js

import React, { useState, useMemo, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

import Header from "./Header";
import Sidebar from "./SideBar";
import { useAuth } from "../context/AuthContext.jsx";

// Modals (migrated locations)
import { InterestRatesModal } from "../../screens/modals/InterestRatesModal.jsx";
import { BuildBudgetModal } from "../../features/townBudget/model/BuildTownBudgetModal.jsx";
import { BuildBoeBudgetModal } from "../../features/boe/model/BuildBoeBudgetModal.jsx";
import { ALL_TOWNS, TOWN_SEEC_ORGANIZATIONS } from "../../shared/constants/townNames";
import {
  SEEC_CANDIDATE_COMMITTEE_NAMES,
  SEEC_CANDIDATE_NAME_TO_CAMPAIGNS,
} from "../../shared/constants";

/**
 * MainLayout
 *
 * Root application layout component responsible for:
 * - Rendering the global Header and Sidebar
 * - Managing responsive sidebar behavior (mobile vs desktop)
 * - Determining active "tab context" (Town Budget vs BoE vs Reports) based on route
 * - Owning shared UI state such as selected fiscal year and modal visibility
 * - Mounting and unmounting modal workflows safely based on active route
 * - Providing layout context to nested routes via React Router <Outlet />
 *
 * This component intentionally centralizes cross-cutting UI concerns
 * (navigation, modals, year selection) so feature-level screens can remain focused.
 *
 * @component
 * @returns {JSX.Element} The main application shell with header, sidebar, routed content, and modals
 */
export function MainLayout() {
  /**
   * Sidebar open/closed state (primarily for mobile layouts).
   * Desktop layouts always render the sidebar open.
   * @type {[boolean, Function]}
   */
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  /**
   * Auth context values for user display and logout behavior.
   */
  const { currentUser, logout } = useAuth();

  /**
   * Currently selected fiscal year.
   * This value is shared with child routes via Outlet context.
   * @type {[string, Function]}
   */
  const [selectedYear, setSelectedYear] = useSharedFilter("layout:selectedYear", "FY2025 - 2026");

  /**
   * Placeholder callback registration hook for downstream components.
   * Exists to support future reset orchestration across nested routes.
   */
  const registerResetCallback = () => {};

  /**
   * React Router location object for route-aware layout behavior.
   */
  const location = useLocation();
  const pathname = location.pathname;
  useEffect(() => { setSidebarOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        document.querySelector('[aria-controls="site-sidebar"]')?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isSidebarOpen]);

  /**
   * Determines whether the current route belongs to the Town Budget domain.
   * Memoized to avoid unnecessary recalculation on unrelated renders.
   */
  const isTownBudgetRoute = useMemo(() => {
    return pathname === "/budget" || pathname === "/town" || pathname.startsWith("/budget/town");
  }, [pathname]);

  /**
   * Determines whether the current route belongs to the Board of Education
   * budget or reporting domains.
   */
  const isBoeBudgetRoute = useMemo(() => {
    return (
      pathname.startsWith("/budget/board-of-education") ||
      pathname.startsWith("/budget/boe") ||
      pathname.startsWith("/reports") ||
      pathname.startsWith("/boe")
    );
  }, [pathname]);

  /**
   * Broad route flags used for shared UI decisions.
   */
  const isComparisonRoute = pathname === "/budget-vs-spent";
  const useBoeBuilder = isBoeBudgetRoute ||
    (isComparisonRoute && (new URLSearchParams(location.search).get('context') || location.state?.budgetContext) === "boe");
  const useTownBuilder = isTownBudgetRoute || (isComparisonRoute && !useBoeBuilder);

  const isBudgetRoute = pathname.startsWith("/budget");
  const isReportsRoute =
    pathname.startsWith("/reports") || pathname.startsWith("/boe");
  const isSeecRoute = pathname.startsWith("/seec");
  const isCrimeRoute = pathname.startsWith("/crime");

  /**
   * Sidebar tools (year picker, build buttons, etc.) should be visible
   * on both budget and reporting routes.
   */
  const showSidebarTools = isTownBudgetRoute || isBudgetRoute || isReportsRoute;
  const seecTowns = useMemo(
    () => [...ALL_TOWNS].sort((a, b) => a.localeCompare(b)),
    []
  );
  const seecCandidates = useMemo(
    () => [...SEEC_CANDIDATE_COMMITTEE_NAMES].sort((a, b) => a.localeCompare(b)),
    [],
  );
  const seecPoliticalCommittees = useMemo(
    () =>
      Array.from(new Set(Object.values(TOWN_SEEC_ORGANIZATIONS).flat())).sort((a, b) =>
        a.localeCompare(b),
      ),
    [],
  );
  const seecPoliticians = useMemo(
    () => Object.keys(SEEC_CANDIDATE_NAME_TO_CAMPAIGNS).sort((a, b) => a.localeCompare(b)),
    [],
  );
  const [selectedSeecPolitician, setSelectedSeecPolitician] = useSharedFilter("layout:selectedSeecPolitician", "");
  const [selectedSeecPoliticalCommittee, setSelectedSeecPoliticalCommittee] = useSharedFilter("layout:selectedSeecPoliticalCommittee", "");
  const [selectedSeecTown, setSelectedSeecTown] = useSharedFilter("layout:selectedSeecTown", "");
  const [selectedSeecCandidate, setSelectedSeecCandidate] = useSharedFilter("layout:selectedSeecCandidate", "");

  /**
   * Inflation Rates modal visibility state.
   */
  const [showInterestRates, setShowInterestRates] = useSharedFilter("layout:showInterestRates", false);

  /**
   * Separate modal visibility states for Town and BoE budget builders.
   * These are intentionally split to prevent cross-route contamination.
   */
  const [showTownBuildBudget, setShowTownBuildBudget] = useState(false);
  const [showBoeBuildBudget, setShowBoeBuildBudget] = useState(false);

  /**
   * Effect guard to prevent modals from remaining open when the user
   * navigates between Town and BoE routes.
   *
   * Ensures:
   * - Town modal is closed when entering BoE routes
   * - BoE modal is closed when entering Town routes
   */
  useEffect(() => {
    if (isTownBudgetRoute && showBoeBuildBudget) {
      setShowBoeBuildBudget(false);
    }

    if (isBoeBudgetRoute && showTownBuildBudget) {
      setShowTownBuildBudget(false);
    }
  }, [
    isTownBudgetRoute,
    isBoeBudgetRoute,
    showTownBuildBudget,
    showBoeBuildBudget,
  ]);

  /**
   * Resets layout UI state.
   * Currently closes the sidebar; can be expanded to reset other
   * layout-level state in the future.
   */
  const handleReset = () => {
    setSidebarOpen(false);
  };

  /**
   * Toggles sidebar open/closed state.
   */
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  /**
   * Opens the appropriate "Build Budget" modal based on the active route.
   * Falls back to Town Budget behavior when invoked from neutral routes.
   */
  const handleOpenBuildBudget = () => {
    if (useTownBuilder) {
      setShowTownBuildBudget(true);
      return;
    }

    if (useBoeBuilder) {
      setShowBoeBuildBudget(true);
      return;
    }

    setShowTownBuildBudget(true);
  };

  const handleSelectSeecPolitician = (politician) => {
    setSelectedSeecPolitician(politician);
    setSelectedSeecPoliticalCommittee("");
    setSelectedSeecTown("");
    setSelectedSeecCandidate("");
  };

  const handleSelectSeecPoliticalCommittee = (committee) => {
    setSelectedSeecPoliticalCommittee(committee);
    setSelectedSeecPolitician("");
    setSelectedSeecTown("");
    setSelectedSeecCandidate("");
  };

  const handleSelectSeecTown = (town) => {
    setSelectedSeecTown(town);
    setSelectedSeecPolitician("");
    setSelectedSeecPoliticalCommittee("");
    setSelectedSeecCandidate("");
  };

  const handleSelectSeecCandidate = (candidate) => {
    setSelectedSeecCandidate(candidate);
    setSelectedSeecPolitician("");
    setSelectedSeecPoliticalCommittee("");
    setSelectedSeecTown("");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        handleReset={handleReset}
        currentUser={currentUser}
        logout={logout}
      />

      <div className="flex flex-1 overflow-hidden relative">
        {(
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 xl:hidden ${
              isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"
            }`}
            onClick={toggleSidebar}
          />
        )}

        <aside
          id="site-sidebar"
          aria-label="Section navigation and tools"
          className={`
            fixed left-0 bg-gray-900 text-white z-50
            w-64 overflow-y-auto overscroll-contain transform transition-transform duration-300
            ${isSidebarOpen ? "translate-x-0 visible" : "-translate-x-full invisible xl:visible"}
            top-14 h-[calc(100vh-3.5rem)] xl:top-0 xl:h-screen xl:translate-x-0
          `}
        >
          <button type="button" onClick={() => { setSidebarOpen(false); document.querySelector('[aria-controls="site-sidebar"]')?.focus(); }} className="xl:hidden m-3 min-h-11 rounded border border-slate-500 bg-transparent px-4 text-white">Close menu</button>
          <Sidebar
            onNavigate={() => setSidebarOpen(false)}
            availableYears={[
              "FY2025 - 2026",
              "FY2024 - 2025",
              "FY2023 - 2024",
              "FY2022 - 2023",
              "FY2021 - 2022",
              "FY2020 - 2021",
            ]}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            handleReset={handleReset}
            showYearButtons={isBudgetRoute}
            showInterestRatesButton={showSidebarTools}
            onOpenInterestRates={() => setShowInterestRates(true)}
            showBuildBudgetButton={showSidebarTools}
            onOpenBuildBudget={handleOpenBuildBudget}
            showCrimeNavigation={isCrimeRoute}
            showSeecTownSelector={isSeecRoute}
            seecPoliticians={seecPoliticians}
            selectedSeecPolitician={selectedSeecPolitician}
            onSelectSeecPolitician={handleSelectSeecPolitician}
            seecPoliticalCommittees={seecPoliticalCommittees}
            selectedSeecPoliticalCommittee={selectedSeecPoliticalCommittee}
            onSelectSeecPoliticalCommittee={handleSelectSeecPoliticalCommittee}
            seecTowns={seecTowns}
            seecCandidates={seecCandidates}
            selectedSeecTown={selectedSeecTown}
            onSelectSeecTown={handleSelectSeecTown}
            selectedSeecCandidate={selectedSeecCandidate}
            onSelectSeecCandidate={handleSelectSeecCandidate}
          />
        </aside>

        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 px-3 sm:px-5 pt-5 pb-16 bg-white text-gray-800 overflow-auto xl:ml-64">
          <Outlet
            context={{
              selectedYear,
              setSelectedYear,
              registerResetCallback,
              selectedSeecPolitician,
              setSelectedSeecPolitician,
              selectedSeecPoliticalCommittee,
              setSelectedSeecPoliticalCommittee,
              selectedSeecTown,
              setSelectedSeecTown,
              selectedSeecCandidate,
              setSelectedSeecCandidate,
            }}
          />
        </main>

        <InterestRatesModal
          open={showInterestRates}
          onClose={() => setShowInterestRates(false)}
          title="Inflation Rates by Year"
          docPath={{ collection: "common", id: "inflationByYear" }}
        />

        {useTownBuilder && (
          <BuildBudgetModal
            open={showTownBuildBudget}
            onClose={() => setShowTownBuildBudget(false)}
            title="Build a Budget Workshop"
            initialYear={new Date().getFullYear() + 1}
            millRateDocPath={{ collection: "common", id: "millRate" }}
            grandListDocPath={{ collection: "common", id: "taxableGrandList" }}
          />
        )}

        {useBoeBuilder && (
          <BuildBoeBudgetModal
            open={showBoeBuildBudget}
            onClose={() => setShowBoeBuildBudget(false)}
            title="Build a BoE Budget Workshop"
          />
        )}
      </div>
    </div>
  );
}
